import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { euro } from "@/lib/api";
import { useSeo, SITE_URL, ORGANIZATION } from "@/lib/seo";
import { Breadcrumb, breadcrumbLd } from "@/components/site/Breadcrumb";
import { Faq, faqLd } from "@/components/site/Faq";
import { OFFERS, UNIT, save12, SavingsTimeline, SubOffers, SUB_FAQ, trackSubCta } from "@/components/subscription/SubOffers";

const PATH = "/abonnement-economies";
const TITLE = "Abonnement capillaire : combien pouvez-vous économiser avec Ozentya ?";
const CRUMBS = [{ to: "/", label: "Accueil" }, { to: "/abonnement", label: "Abonnement" }, { to: PATH, label: "Vos économies" }];
const P = "mt-4 text-base leading-relaxed text-[var(--parchment)]/90";
const H2 = "mt-16 font-display text-3xl leading-tight md:text-4xl";

function YearTable() {
  return (
    <div className="mt-6 overflow-x-auto rounded-sm border border-[var(--line)]">
      <table data-testid="savings-year-table" className="w-full min-w-[540px] text-left text-sm">
        <thead className="bg-[var(--ink-2)] text-xs uppercase tracking-[0.14em] text-[var(--gold-soft)]">
          <tr><th className="p-4">Formule</th><th className="p-4">Achat au tarif habituel</th><th className="p-4">Avec abonnement</th><th className="p-4">Économie sur 12 mois</th></tr>
        </thead>
        <tbody>
          {[...OFFERS].reverse().map((o) => (
            <tr key={o.id} className="border-t border-[var(--line)]">
              <td className="p-4 font-semibold">{o.name} : {o.picks > 1 ? `${o.picks} produits` : "1 produit"}/mois</td>
              <td className="p-4">{euro(o.compare * 12)}/an</td>
              <td className="p-4">{euro(o.price * 12)}/an</td>
              <td className="p-4 font-display text-xl text-[var(--gold-soft)]">{euro(save12(o))}/an</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FinalCta() {
  return (
    <section data-testid="savings-final-cta" className="mt-20 rounded-sm border-2 border-[var(--gold)] bg-[rgba(217,180,106,0.08)] p-6 md:p-10">
      <h2 className="font-display text-3xl leading-tight md:text-4xl">Et si votre routine vous coûtait moins cher chaque mois ?</h2>
      <p className={P}>Si vous achetez déjà régulièrement vos produits Ozentya, comparez vos dépenses avec nos abonnements. Choisissez la formule qui correspond à vos besoins et profitez d'un tarif réduit sur chaque renouvellement.</p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        {OFFERS.map((o, i) => (
          <Link key={o.id} to={`/abonnement?formule=${o.id}`} onClick={() => trackSubCta("savings", o.id)} data-testid={`savings-cta-${o.id}`}
            className={`inline-flex h-14 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold uppercase tracking-[0.16em] transition-colors ${i === 0 ? "bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--gold-soft)]" : "border border-[var(--gold)] text-[var(--gold-soft)] hover:bg-[rgba(217,180,106,0.12)]"}`}>
            Découvrir l'offre {o.name} <ArrowRight className="h-4 w-4" />
          </Link>
        ))}
      </div>
      <p className="mt-4 text-sm text-[var(--parchment)]/85">Voir aussi : <Link to="/boutique" className="text-[var(--gold-soft)] underline underline-offset-4">nos soins à l'unité éligibles</Link> · <Link to="/abonnement" className="text-[var(--gold-soft)] underline underline-offset-4">la page abonnement</Link></p>
    </section>
  );
}

export default function SubSavings() {
  useSeo({
    title: "Abonnement capillaire : combien économiser ? | Ozentya", path: PATH,
    description: "Duo 19,99 €/mois (2 soins) ou Famille 39,90 €/mois (4 soins) au lieu de 16,99 € par produit : jusqu'à 167,88 € ou 336,72 € d'économies sur 12 mois. Tableaux, calcul et FAQ.",
    jsonLd: { "@context": "https://schema.org", "@graph": [
      { "@type": "Article", headline: TITLE, inLanguage: "fr", author: ORGANIZATION, publisher: ORGANIZATION, mainEntityOfPage: `${SITE_URL}${PATH}` },
      breadcrumbLd(SITE_URL, CRUMBS), faqLd(SUB_FAQ),
    ] },
  });
  return (
    <main data-testid="sub-savings-page" className="px-5 pb-32 pt-40 md:px-10">
      <article className="mx-auto max-w-4xl">
        <Breadcrumb items={CRUMBS} />
        <p className="eyebrow mb-4">Abonnement Ozentya</p>
        <h1 className="font-display text-4xl leading-[1.08] sm:text-5xl lg:text-6xl">{TITLE}</h1>
        <p className={P}>Lorsque l'on utilise régulièrement des produits capillaires, les achats peuvent rapidement s'accumuler au fil des mois. Plutôt que de commander chaque produit séparément au tarif habituel de {euro(UNIT)}, Ozentya vous permet d'opter pour une formule mensuelle adaptée à votre routine. Découvrez combien vous pouvez économiser avec nos abonnements Duo et Famille.</p>

        <h2 className={H2}>Pourquoi acheter chaque mois au tarif habituel ?</h2>
        <p className={P}>Quand on suit une routine, on renouvelle ses produits au fil du temps. Racheter le même soin à l'unité chaque mois revient plus cher qu'une formule mensuelle : avec l'abonnement, vous recevez les produits que vous avez choisis, à un tarif réduit, sans y penser. Chacune a ses besoins : la fréquence dépend de vos produits et de leurs conseils d'utilisation, et tout le monde n'a pas besoin de deux produits par mois.</p>

        <h2 className={H2}>Comparaison des prix sur 12 mois</h2>
        <YearTable />
        <p className="mt-3 text-sm text-[var(--parchment)]/80">Les économies sont calculées sur la base d'un prix habituel de 16,99 € par produit et de 12 mensualités au tarif indiqué. Elles supposent que le client aurait acheté le même nombre de produits au tarif habituel.</p>

        <h2 className={H2}>Quelle formule choisir ?</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-sm border border-[var(--line)] p-6"><p className="eyebrow mb-2">Famille · 39,90 €/mois</p><p className="text-base text-[var(--parchment)]/90">Pour recevoir 4 soins au choix chaque mois (jusqu'à 16,99 € chacun) : jusqu'à 28,06 € économisés à chaque envoi. Livraison 3,50 € en Point Relais.</p></div>
          <div className="rounded-sm border-2 border-[var(--gold)] p-6"><p className="eyebrow mb-2">Duo · 19,99 €/mois</p><p className="text-base text-[var(--parchment)]/90">Pour celles qui achètent déjà plusieurs produits régulièrement et veulent optimiser leur budget : l'économie annuelle la plus forte (167,88 €), si vous utilisez réellement les deux produits chaque mois.</p></div>
        </div>

        <div className="mt-16"><SavingsTimeline /></div>

        <h2 className={H2}>Pourquoi la régularité compte dans une routine capillaire</h2>
        <p className={P}>Une routine capillaire s'inscrit généralement dans la durée : on juge un soin sur plusieurs semaines, pas sur quelques jours. Certains objectifs, notamment liés à la chute des cheveux ou à l'alopécie, demandent un suivi adapté, parfois médical : leurs causes sont variées et ne se prennent pas toutes en charge de la même façon. Nos soins sont des cosmétiques ; ils ne garantissent ni repousse ni guérison, et leur intérêt dépend du produit choisi et de la cause de la chute. En cas de chute importante, consultez un dermatologue.</p>
        <p className={P}>L'abonnement n'est pas une nécessité : il facilite simplement le renouvellement régulier des produits que vous avez choisi d'utiliser, tout en vous faisant économiser.</p>

        <h2 className={H2}>Les deux offres en détail</h2>
        <div className="mt-6"><SubOffers placement="savings" /></div>

        <FinalCta />
        <Faq items={SUB_FAQ} title="Questions fréquentes sur l'abonnement" />
      </article>
    </main>
  );
}
